import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { dealCondition, dealHeadline, dealNeedsCode, dealThreshold } from "../../utils/dealText";
import { resizedImage } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS, VISUALLY_HIDDEN } from "../../utils/designTokens";

/** platform | restaurant | catering — which colour and which action a deal gets. */
export const dealGroup = (p) => (p.isCatering ? "catering" : p.restaurantId ? "restaurant" : "platform");

const GROUP = {
  platform: { bg: C.red, label: "RushMyFood", action: "Browse restaurants" },
  restaurant: { bg: C.teal, label: "Restaurant", action: "Order now" },
  catering: { bg: C.green, label: "Catering", action: "View catering menu" },
};

/** "$ off order", "% off order", "Free item"… */
export function dealTypeLabel(p) {
  if (p.promotionType === "LOYALTY_REWARD") return "Rewards";
  switch (p.rewards?.[0]?.rewardType) {
    case "PERCENT_OFF": return "% off order";
    case "AMOUNT_OFF": return "$ off order";
    case "FREE_ITEM": return "Free item";
    case "CHEAPEST_ITEM_FREE": return "Buy one, get one";
    case "FREE_DELIVERY": return "Free delivery";
    case "GENERATE_VOUCHER": return "Voucher";
    default: return "Deal";
  }
}

// Acronyms that stay in capitals when a title is put in sentence case.
const KEEP_CAPS = new Set(["BOGO", "BBQ", "USA", "VIP", "NY", "NYC", "DIY", "XL", "XXL"]);
// Everyday words a Title Case name capitalises; dish and restaurant names keep theirs.
const COMMON = new Set(
  "a an and any at buy each for free from get in of off on one or order orders per plus spend the to two with your".split(" ")
);

/**
 * Sentence case for an admin-written title: all-capital words and everyday
 * words come down ("Get a FREE Order of" → "get a free order of"); names stay
 * as written; the first letter is a capital.
 */
export function sentenceCase(text) {
  const words = String(text || "").trim().split(/(\s+)/);
  let first = true;
  return words
    .map((w) => {
      if (/^\s+$/.test(w) || !w) return w;
      const letters = w.replace(/[^A-Za-z]/g, "");
      let out = w;
      if (letters.length >= 2 && letters === letters.toUpperCase() && !KEEP_CAPS.has(letters)) out = w.toLowerCase();
      else if (COMMON.has(letters.toLowerCase())) out = w.toLowerCase();
      if (first) {
        out = out.charAt(0).toUpperCase() + out.slice(1);
        first = false;
      }
      return out;
    })
    .join("");
}

function CopyCode({ code, color }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(code);
    } catch (e) {
      // Clipboard blocked: the code is on the button to read.
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };
  return (
    <>
    <Box
      component="button"
      type="button"
      onClick={copy}
      aria-label={`Copy code ${code}`}
      sx={{
        ...BUTTON_RESET,
        ...FOCUS_RING,
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        height: 44,
        px: 1.5,
        minWidth: 0,
        borderRadius: "12px",
        border: `1.5px dashed ${color}`,
        color,
        fontFamily: FONT_BODY,
        "&:hover": { bgcolor: C.surface },
      }}
    >
      <Box component="span" sx={{ fontSize: 14, fontWeight: 800, letterSpacing: "0.06em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{code}</Box>
      <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13, fontWeight: 600, color: copied ? C.green : C.muted, whiteSpace: "nowrap" }}>
        {copied ? (
          <>
            Copied <CheckRoundedIcon sx={{ fontSize: 16 }} />
          </>
        ) : (
          <>
            <ContentCopyRoundedIcon sx={{ fontSize: 15 }} /> Copy
          </>
        )}
      </Box>
    </Box>
    {/* Outside the button: some screen readers skip a live region inside one. */}
    <Box component="span" role="status" sx={VISUALLY_HIDDEN}>
      {copied ? "Copied" : ""}
    </Box>
    </>
  );
}

/**
 * One deal. `cartSubtotal` is the open cart's subtotal when this deal can
 * apply to it (null otherwise); `onAction` is the red button.
 */
function DealCard({ promo, currency = "$", cartSubtotal, onAction }) {
  const group = dealGroup(promo);
  const look = GROUP[group];
  const headline = dealHeadline(promo);
  const line = dealCondition(promo);
  const threshold = dealThreshold(promo);
  const photo = promo.restaurantImage || promo.restaurantLogo;
  const title = sentenceCase(promo.name);
  const short = (n) => `${currency}${Number(n).toFixed(2)}`;

  return (
    <Box
      component="article"
      sx={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: "22px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "relative", height: 150, boxSizing: "border-box", p: "20px 22px", bgcolor: look.bg, color: "#FFFFFF", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Box aria-hidden="true" sx={{ position: "absolute", width: 180, height: 180, borderRadius: "50%", right: -50, top: -70, bgcolor: "rgba(255,255,255,0.12)" }} />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box component="span" sx={{ flexShrink: 0, height: 24, px: 1, display: "inline-flex", alignItems: "center", borderRadius: RADIUS.pill, bgcolor: "rgba(255,255,255,0.18)", fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {look.label}
          </Box>
          <Box component="span" sx={{ minWidth: 0, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.82)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {dealTypeLabel(promo)}
          </Box>
        </Box>
        <Box sx={{ position: "relative", mt: "auto" }}>
          <Box component="p" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: headline.length > 14 ? 28 : 40, lineHeight: 1.02, letterSpacing: "-0.02em", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {headline}
          </Box>
          {line && (
            <Box component="p" sx={{ m: 0, mt: 0.5, fontFamily: FONT_BODY, fontSize: 14, color: "rgba(255,255,255,0.9)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {line}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, sm: "18px 20px" }, display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
        <Box component="h3" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, lineHeight: 1.35, color: C.ink, overflowWrap: "anywhere" }}>
          {title}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          {group === "platform" || !photo ? (
            <Box aria-hidden="true" sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", bgcolor: group === "platform" ? C.redTint : C.surface2, color: group === "platform" ? C.red : C.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {group === "platform" ? <ShoppingBagRoundedIcon sx={{ fontSize: 20 }} /> : <LocalOfferOutlinedIcon sx={{ fontSize: 19 }} />}
            </Box>
          ) : (
            <Box component="img" src={resizedImage(photo, 128)} alt="" loading="lazy" decoding="async" sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "10px", objectFit: "cover", bgcolor: C.surface2 }} />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500, color: C.muted }}>Where</Box>
            <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {promo.restaurantId ? promo.restaurantName || "This restaurant" : "All restaurants"}
            </Box>
          </Box>
        </Box>

        {cartSubtotal != null && threshold != null && (
          cartSubtotal >= threshold ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: "12px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600 }}>
              <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 19 }} />
              Your cart ({short(cartSubtotal)}) qualifies
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderRadius: "12px", bgcolor: C.goldSoft, color: C.gold, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600 }}>
              <LocalOfferOutlinedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
              Add {short(threshold - cartSubtotal)} more to qualify
            </Box>
          )
        )}

        <Box sx={{ mt: "auto", pt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          {dealNeedsCode(promo) ? (
            <CopyCode code={promo.couponCode} color={look.bg} />
          ) : (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.muted }}>
              <CreditCardRoundedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
              Apply at checkout
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={() => onAction(promo)}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              flexShrink: 0,
              height: 44,
              px: 2,
              borderRadius: RADIUS.pill,
              bgcolor: C.red,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: C.redHover },
            }}
          >
            {look.action}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(DealCard);
